<template>
  <div class="modalities-page">
    <div v-if="hasChanges && !isReadOnly" class="save-button-container">
      <button
        type="button"
        class="btn-save"
        @click="saveChanges"
        :disabled="saving"
      >
        <i class="fa-solid fa-save"></i>
        {{ saving ? 'Salvando...' : 'Salvar Alterações' }}
      </button>
    </div>

    <div class="page-hint">
      <i class="fa-solid fa-circle-info"></i>
      <p>
        Cada região pode ter <strong>apenas uma modalidade</strong> cadastrada.
      </p>
    </div>

    <div v-if="visibleModalities.length === 0" class="empty-state">
      <div class="empty-card">
        <i class="fa-solid fa-layer-group"></i>
        <h4>Nenhuma modalidade cadastrada</h4>
        <p>Crie a modalidade da região para configurar cotação, prêmio máximo e ativação.</p>
        <button
          v-if="canAddModality"
          type="button"
          class="btn-add"
          :disabled="isReadOnly"
          @click="addModality()"
        >
          <i class="fa-solid fa-plus"></i>
          Adicionar modalidade
        </button>
      </div>
    </div>

    <div v-else class="modalities-board">
      <section
        v-for="(m, index) in visibleModalities"
        :key="getModalityKey(m, index)"
        class="modality-card"
        :class="{
          readonly: isReadOnly,
          'is-new': m.operation === 'Add',
          'is-modified': m.operation === 'Update' && m.hasChanges,
          'is-existing': m.operation === 'Update' && !m.hasChanges,
        }"
      >
        <header class="modality-card-header">
          <div class="modality-card-title">
            <span class="modality-order-badge">#{{ m.sortOrder || index + 1 }}</span>
            <div class="modality-name-wrap">
              <input
                type="text"
                class="modality-name-input"
                v-model="m.name"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markAsChanged(m)"
                placeholder="Digite o nome da modalidade"
              />
              <span class="modality-count-text">
                {{ getVisibleDetailCount(m) }} item(ns) vinculado(s)
              </span>
            </div>
          </div>

          <div class="modality-card-actions">
            <span
              class="status-chip"
              :class="statusChipClass(m)"
            >
              {{ statusChipLabel(m) }}
            </span>
            <button
              type="button"
              class="btn-delete"
              :disabled="isReadOnly"
              @click="deleteModality(m)"
              title="Excluir modalidade"
            >
              <i class="fa-solid fa-trash"></i>
              Excluir
            </button>
          </div>
        </header>

        <div class="table-scroll">
          <table class="modalities-table">
            <thead>
              <tr>
                <th class="th-position">Posição</th>
                <th>Modalidade</th>
                <th>Cotação</th>
                <th>Prêmio Máx.</th>
                <th>Ativo</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(d, detailIndex) in getOrderedDetails(m)"
                :key="getDetailKey(d, detailIndex)"
                :class="{ 'detail-row-dragging': draggedDetailKey === getDetailDragKey(m, d, detailIndex) }"
                :draggable="!isReadOnly && !isDetailFieldDisabled(m)"
                @dragstart="handleDetailDragStart(m, d, detailIndex)"
                @dragend="clearDetailDragState"
                @dragover.prevent
                @drop="handleDetailDrop(m, d, detailIndex)"
              >
                <td class="td-position">
                  <div class="detail-position-cell">
                    <i
                      class="fa-solid fa-grip-vertical detail-drag-handle"
                      :class="{ disabled: isReadOnly || isDetailFieldDisabled(m) }"
                      title="Arraste para reordenar"
                    ></i>
                    <input
                      type="number"
                      class="position-input"
                      :value="d.sortOrder || detailIndex + 1"
                      min="1"
                      :max="getVisibleDetailCount(m)"
                      :disabled="isReadOnly || isDetailFieldDisabled(m)"
                      :readonly="isReadOnly || isDetailFieldDisabled(m)"
                      @change="handleDetailPositionChange(m, d, $event)"
                    />
                  </div>
                </td>
                <td class="td-modality-name">{{ d.modalityName }}</td>
                <td>
                  <input
                    type="number"
                    class="cell-input"
                    v-model="d.price"
                    :disabled="isReadOnly || isDetailFieldDisabled(m)"
                    :readonly="isReadOnly || isDetailFieldDisabled(m)"
                    @input="markAsChanged(m)"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    class="cell-input"
                    v-model="d.maxPrize"
                    :disabled="isReadOnly || isDetailFieldDisabled(m)"
                    :readonly="isReadOnly || isDetailFieldDisabled(m)"
                    @input="markAsChanged(m)"
                  />
                </td>
                <td class="td-active">
                  <input
                    type="checkbox"
                    class="active-checkbox"
                    v-model="d.isActive"
                    :disabled="isReadOnly || isDetailFieldDisabled(m)"
                    @change="markAsChanged(m)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    regionModalitiesParameter: {
      type: Array,
      required: true,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ["addModality", "save-changes"],
  data: () => ({
    saving: false,
    draggedDetailKey: null
  }),
  computed: {
    visibleModalities() {
      if (!this.regionModalitiesParameter || !Array.isArray(this.regionModalitiesParameter)) {
        return [];
      }
      return this.regionModalitiesParameter
        .filter(m => m.operation !== 'Delete')
        .filter(m =>
          m.operation === 'Add' ||
          this.getVisibleDetailCount(m) > 0
        )
        .sort((a, b) => {
          const orderA = Number.isFinite(a.sortOrder) ? a.sortOrder : Number.MAX_SAFE_INTEGER;
          const orderB = Number.isFinite(b.sortOrder) ? b.sortOrder : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return (a.name || '').localeCompare(b.name || '');
        });
    },

    // Só permite criar quando ainda não existe nenhuma
    canAddModality() {
      if (!this.regionModalitiesParameter || !Array.isArray(this.regionModalitiesParameter)) {
        return true;
      }
      return this.regionModalitiesParameter.every(m => m.operation === 'Delete');
    },

    hasEmptyModality() {
      if (!this.regionModalitiesParameter || !Array.isArray(this.regionModalitiesParameter)) {
        return false;
      }
      return this.regionModalitiesParameter.some(
        m => m.operation === 'Add' && (!m.name || m.name.trim() === '')
      );
    },

    hasChanges() {
      if (!this.regionModalitiesParameter || !Array.isArray(this.regionModalitiesParameter)) {
        return false;
      }
      return this.regionModalitiesParameter.some(
        m => m.operation === 'Add' || m.operation === 'Delete' || m.hasChanges === true
      );
    }
  },
  methods: {
    statusChipLabel(modality) {
      if (modality.operation === 'Add') return 'Nova';
      if (modality.operation === 'Update' && modality.hasChanges) return 'Modificada';
      return 'Existente';
    },

    statusChipClass(modality) {
      if (modality.operation === 'Add') return 'tone-add';
      if (modality.operation === 'Update' && modality.hasChanges) return 'tone-update';
      return 'tone-existing';
    },

    getModalityKey(modality, index = 0) {
      return modality.id || modality.tempId || `new-${index}`;
    },

    getDetailKey(detail, index = 0) {
      return detail.modalityId || `detail-${index}`;
    },

    getDetailDragKey(modality, detail, index = 0) {
      return `${this.getModalityKey(modality)}::${this.getDetailKey(detail, index)}`;
    },

    isManagedLottery(detail) {
      const name = (detail?.modalityName || '')
        .trim()
        .toUpperCase();
      // Gerenciadas em Cotação Loteria (Lotinha/Quininha/Seninha/Binguinho/Bingo Semanal/Loto Quina).
      return [
        'LOTINHA',
        'QUININHA',
        'SENINHA',
        'BINGUINHO',
        'BINGO SEMANAL',
        'LOTO QUINA',
      ].includes(name);
    },

    getVisibleDetailCount(modality) {
      const details = Array.isArray(modality?.regionModalityDetail)
        ? modality.regionModalityDetail
        : [];
      return details.filter(detail => !this.isManagedLottery(detail)).length;
    },

    getOrderedDetails(modality) {
      const details = Array.isArray(modality.regionModalityDetail)
        ? modality.regionModalityDetail
        : [];

      return details
        .filter(detail => !this.isManagedLottery(detail))
        .sort((a, b) => {
          const orderA = Number.isFinite(a.sortOrder) && a.sortOrder > 0 ? a.sortOrder : Number.MAX_SAFE_INTEGER;
          const orderB = Number.isFinite(b.sortOrder) && b.sortOrder > 0 ? b.sortOrder : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return (a.modalityName || '').localeCompare(b.modalityName || '');
        });
    },

    handleDetailDragStart(modality, detail, index) {
      if (this.isReadOnly || this.isDetailFieldDisabled(modality)) {
        return;
      }

      this.draggedDetailKey = this.getDetailDragKey(modality, detail, index);
    },

    clearDetailDragState() {
      this.draggedDetailKey = null;
    },

    handleDetailDrop(modality, targetDetail, targetIndex) {
      if (this.isReadOnly || this.isDetailFieldDisabled(modality) || !this.draggedDetailKey) {
        return;
      }

      const orderedDetails = [...this.getOrderedDetails(modality)];
      const draggedIndex = orderedDetails.findIndex(
        (detail, index) => this.getDetailDragKey(modality, detail, index) === this.draggedDetailKey
      );
      const destinationIndex = orderedDetails.findIndex(
        (detail, index) => this.getDetailDragKey(modality, detail, index) === this.getDetailDragKey(modality, targetDetail, targetIndex)
      );

      if (draggedIndex === -1 || destinationIndex === -1 || draggedIndex === destinationIndex) {
        this.clearDetailDragState();
        return;
      }

      const [draggedDetail] = orderedDetails.splice(draggedIndex, 1);
      orderedDetails.splice(destinationIndex, 0, draggedDetail);
      this.applyDetailOrder(modality, orderedDetails);
      this.clearDetailDragState();
    },

    applyDetailOrder(modality, orderedDetails) {
      let changed = false;

      orderedDetails.forEach((detail, index) => {
        const nextOrder = index + 1;
        if (detail.sortOrder !== nextOrder) {
          detail.sortOrder = nextOrder;
          changed = true;
        }
      });

      if (changed) {
        this.markAsChanged(modality);
      }
    },

    handleDetailPositionChange(modality, detail, event) {
      if (this.isReadOnly || this.isDetailFieldDisabled(modality)) {
        return;
      }

      const orderedDetails = [...this.getOrderedDetails(modality)];
      const currentIndex = orderedDetails.findIndex(item => this.getDetailKey(item) === this.getDetailKey(detail));
      if (currentIndex === -1) {
        return;
      }

      const requestedPosition = Number(event?.target?.value ?? detail.sortOrder);
      const safePosition = Number.isFinite(requestedPosition) ? Math.trunc(requestedPosition) : currentIndex + 1;
      const clampedPosition = Math.min(Math.max(safePosition, 1), orderedDetails.length);

      const [movedDetail] = orderedDetails.splice(currentIndex, 1);
      orderedDetails.splice(clampedPosition - 1, 0, movedDetail);
      this.applyDetailOrder(modality, orderedDetails);
    },

    isDetailFieldDisabled(modality) {
      return modality.operation === 'Add' && (!modality.name || modality.name.trim() === '');
    },

    markAsChanged(modality) {
      if (!this.isReadOnly && modality.operation === 'Update') {
        modality.hasChanges = true;
      }
    },

    deleteModality(modality) {
      if (this.isReadOnly) {
        return;
      }

      if (modality.operation == "Add") {
        let index = this.regionModalitiesParameter.indexOf(modality);
        this.regionModalitiesParameter.splice(index, 1);
      } else if (modality.operation == "Update") {
        modality.operation = "Delete";
      } else if (modality.operation == "Delete") {
        modality.operation = "Update";
      }
    },

    addModality() {
      if (this.isReadOnly || !this.canAddModality) {
        return;
      }

      this.$emit("addModality");
    },

    saveChanges() {
      if (this.isReadOnly || this.saving) {
        return;
      }

      this.saving = true;
      this.$emit("save-changes");
    },

    resetSaving() {
      this.saving = false;
      this.draggedDetailKey = null;
      if (this.regionModalitiesParameter) {
        this.regionModalitiesParameter.forEach(m => {
          m.hasChanges = false;
        });
      }
    }
  },
};
</script>

<style scoped>
.modalities-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.save-button-container {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
  margin: -0.25rem 0 0.25rem;
  background: var(--card-background);
  border-bottom: 1px solid var(--border-color);
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  border: none;
  border-radius: 8px;
  background: var(--success, #28a745);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;
}

.btn-save:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.page-hint {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.page-hint i {
  color: var(--primary-color);
  margin-top: 0.15rem;
}

.page-hint p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.page-hint strong {
  color: var(--text-primary);
}

.empty-state {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 1rem;
}

.empty-card {
  width: 100%;
  max-width: 480px;
  text-align: center;
  padding: 2rem 1.5rem;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  box-shadow: var(--shadow-sm);
}

.empty-card i {
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 0.75rem;
}

.empty-card h4 {
  margin: 0 0 0.5rem;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.empty-card p {
  margin: 0 0 1.25rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.45;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.1rem;
  border: none;
  border-radius: 8px;
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.btn-add:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.modalities-board {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modality-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.modality-card.is-new {
  border-color: color-mix(in srgb, var(--primary-color) 45%, var(--border-color));
}

.modality-card.is-modified {
  border-color: color-mix(in srgb, #f0ad4e 50%, var(--border-color));
}

.modality-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.1rem;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.modality-card-title {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.modality-order-badge {
  min-width: 42px;
  padding: 0.35rem 0.55rem;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  flex-shrink: 0;
}

.modality-name-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.modality-name-input {
  width: 100%;
  max-width: 420px;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--input-background, var(--card-background));
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
}

.modality-name-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--primary-color-10);
}

.modality-name-input::placeholder {
  color: var(--text-muted);
  font-weight: 400;
  font-style: italic;
}

.modality-count-text {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.modality-card-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border: 1px solid transparent;
}

.status-chip.tone-add {
  color: var(--primary-color);
  background: var(--primary-color-10);
  border-color: color-mix(in srgb, var(--primary-color) 30%, transparent);
}

.status-chip.tone-update {
  color: #b8860b;
  background: color-mix(in srgb, #f0ad4e 16%, transparent);
  border-color: color-mix(in srgb, #f0ad4e 35%, transparent);
}

.status-chip.tone-existing {
  color: #1e7e34;
  background: color-mix(in srgb, #28a745 14%, transparent);
  border-color: color-mix(in srgb, #28a745 30%, transparent);
}

.btn-delete {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, #dc3545 35%, var(--border-color));
  background: color-mix(in srgb, #dc3545 10%, transparent);
  color: #dc3545;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.btn-delete:hover:not(:disabled) {
  opacity: 0.85;
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-scroll {
  overflow-x: auto;
}

.modalities-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 560px;
}

.modalities-table th,
.modalities-table td {
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
  color: var(--text-primary);
}

.modalities-table thead th {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
  white-space: nowrap;
}

.th-position {
  width: 110px;
}

.td-modality-name {
  text-align: left;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
}

.td-position,
.td-active {
  text-align: center;
}

.detail-position-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
}

.detail-drag-handle {
  color: var(--text-muted);
  cursor: grab;
}

.detail-drag-handle.disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.position-input,
.cell-input {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--input-background, var(--card-background));
  color: var(--text-primary);
  padding: 0.4rem 0.5rem;
  font-size: 0.9rem;
}

.position-input {
  width: 52px;
  text-align: center;
}

.cell-input {
  width: 90px;
  text-align: right;
}

.position-input:focus,
.cell-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--primary-color-10);
}

.position-input:disabled,
.position-input[readonly],
.cell-input:disabled,
.cell-input[readonly],
.modality-name-input:disabled,
.modality-name-input[readonly] {
  opacity: 0.65;
  cursor: not-allowed;
  background: var(--bg-secondary);
}

.active-checkbox {
  width: 18px;
  height: 18px;
  accent-color: var(--success, #28a745);
  cursor: pointer;
}

.active-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.detail-row-dragging {
  opacity: 0.55;
}

@media (max-width: 768px) {
  .modality-card-header {
    flex-direction: column;
  }

  .modality-name-input {
    max-width: none;
  }
}
</style>
